import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import Icon from '../../../../ui/global/Icon';
import ArtAsset from './ArtAsset';
import ActionCard from './ActionCard';
import { useArtList, Art } from '../../../../backend/useArtList';
import { useUser } from '../../../../backend/useUser';
import LoadingSpinner from '../../../../ui/components/LoadingSpinner';
import Button from '../../../../ui/components/Button';
import Tooltip from '../../../../ui/components/Tooltip';
import MenuPanel from '../../../../ui/components/MenuPanel';
import Search from './Search';
import { useMediaQuery } from '@mui/material';

const Root = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    max-width: 40vw;
    height: 100%;
    line-height: 1.2em;
    gap: 10px;

    @media (max-width: 650px) {
        max-width: 80vw;
    }
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0 7px 0 12px;
    margin: 0 auto -5px auto;
    gap: 10px;
    z-index: 100;
`;

const Title = styled.h4`
    width: 100%;
    text-align: left;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 14px;
    color: ${Colors.grey2};
    padding: 0 4px;
    margin: 0;
    z-index: 10;
`;

const Label = styled.label`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    flex: 1;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    width: 100%;
    height: auto;
    gap: 12px;
    align-items: start;
    
    @media (min-width: 768px) {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }

    @media (min-width: 480px) {
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    }
`;

const LoadingRef = styled.div`
    position: relative;
    height: 100px;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 10px;
    // border: 1px solid red;
`;

const Together = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 5px;
    // margin: auto;
`;

const EmptyStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    text-align: center;
    padding: 10px;
    gap: 12px;
`;

const EmptyStateText = styled.p`
    max-width: 100%;
    width: 190px;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    color: ${Colors.grey4};
    font-size: 14px;
    line-height: 1.2em;
    padding: 0;
    margin: 0;
`;

interface ArtMenuProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    selectedArtIndex?: number | null;
    onArtSelected: (artId: string, aiImage: string, artName: string) => void;
    onArtUploaded: (asset: string) => void;
    isGeneratingGame: boolean;
    isUpdatingGameArt: boolean;
    onAutoGenerateClick?: () => void;
}

const ITEMS_PER_PAGE = 16;

const ArtMenu: React.FC<ArtMenuProps> = ({ 
  isOpen, 
  onOpenChange, 
  selectedArtIndex, 
  onArtSelected, 
  onArtUploaded, 
  isGeneratingGame, 
  isUpdatingGameArt,
  onAutoGenerateClick 
}) => {
  const { listMyArt2D, listAllArt2D } = useArtList();
  const { user, loading: userLoading } = useUser();
  const [myArt, setMyArt] = useState<Art[]>([]);
  const [communityArt, setCommunityArt] = useState<Art[]>([]);
  const [loading, setLoading] = useState(true);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [communityPage, setCommunityPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [communityHasMore, setCommunityHasMore] = useState(true);
  const loadingRef = useRef(null);
  const communityLoadingRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMyArt, setFilteredMyArt] = useState<Art[]>([]);
  const [filteredCommunityArt, setFilteredCommunityArt] = useState<Art[]>([]);
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Add this useEffect to filter art based on search term
  useEffect(() => {
    // Only filter if there's something typed in the search box
    if (searchTerm.trim() !== '') {
      const searchTermLower = searchTerm.trim().toLowerCase();
      
      // Filter my art
      const filteredMy = myArt.filter(art => 
        art.art_name.toLowerCase().includes(searchTermLower)
      );
      setFilteredMyArt(filteredMy);
      
      // Filter community art
      const filteredCommunity = communityArt.filter(art => 
        art.art_name.toLowerCase().includes(searchTermLower)
      );
      setFilteredCommunityArt(filteredCommunity);
    } else {
      // If search box is empty, show all art
      setFilteredMyArt(myArt);
      setFilteredCommunityArt(communityArt);
    }
  }, [searchTerm, myArt, communityArt]);

  const loadArt = useCallback(async (pageNum: number) => {
    if (userLoading || !user) return;

    setLoading(true);
    try {
      const { arts, hasMore: more } = await listMyArt2D(user.id, pageNum, ITEMS_PER_PAGE, true);
      setMyArt(prev => pageNum === 1 ? arts : [...prev, ...arts]);
      setHasMore(more);
    } catch (error) {
      console.error('Error loading art:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [listMyArt2D, user, userLoading]);

  const loadCommunityArt = useCallback(async (pageNum: number) => {
    if (userLoading || !user) return;

    setCommunityLoading(true);
    try {
      const { arts, hasMore: more } = await listAllArt2D(pageNum, ITEMS_PER_PAGE, user.id, true);
      setCommunityArt(prev => pageNum === 1 ? arts : [...prev, ...arts]);
      setCommunityHasMore(more);
    } catch (error) {
      console.error('Error loading community art:', error);
      setCommunityHasMore(false);
    } finally {
      setCommunityLoading(false);
    }
  }, [listAllArt2D, user, userLoading]);

  useEffect(() => {
    if (user) {
      setPage(1);
      loadArt(1);
      setCommunityPage(1);
      loadCommunityArt(1);
    }
  }, [loadArt, loadCommunityArt, user]);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadArt(nextPage);
    }
  }, [loading, hasMore, page, loadArt]);

  const handleLoadMoreCommunity = useCallback(() => {
    if (!communityLoading && communityHasMore) {
      const nextPage = communityPage + 1;
      setCommunityPage(nextPage);
      loadCommunityArt(nextPage);
    }
  }, [communityLoading, communityHasMore, communityPage, loadCommunityArt]);

  // Set up intersection observer for infinite scrolling - My Art
  useEffect(() => {
    const currentRef = loadingRef.current;
    
    if (!currentRef || !hasMore || loading) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        // Only trigger once when element becomes visible and we're not already loading
        if (entries[0].isIntersecting && hasMore && !loading) {
          // console.log('Intersection observed, loading more...');
          handleLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: '20px' }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, handleLoadMore]);

  // Set up intersection observer for infinite scrolling - Community Art
  useEffect(() => {
    const currentRef = communityLoadingRef.current;
    
    if (!currentRef || !communityHasMore || communityLoading || hasMore) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && communityHasMore && !communityLoading && !hasMore) {
          handleLoadMoreCommunity();
        }
      },
      { threshold: 0.1, rootMargin: '20px' }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [communityHasMore, communityLoading, handleLoadMoreCommunity, hasMore]);

  const handleCreateNewArt = () => {
    // console.log('Create new art clicked');
    // Open a new browser tab to the art creation page with a parameter to auto-create 2D art
    window.open('/art?new=2d', '_blank');
    onOpenChange(false);
  };

  const handleUploadImage = () => {
    // Create a hidden file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*'; // Accept only image files
    fileInput.style.display = 'none';
    
    // Add event listener for when a file is selected
    fileInput.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const selectedFile = target.files[0];
        // console.log('Selected file:', selectedFile.name);
        
        // Convert the file to base64
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64String = e.target?.result as string;
          // Pass the base64 data to the callback function
          onArtUploaded(base64String);
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
        };
        reader.readAsDataURL(selectedFile);
      }
    });
    
    // Append to body, click to open file dialog, then remove
    document.body.appendChild(fileInput);
    fileInput.click();
    
    // Remove the element after selection (or cancel)
    setTimeout(() => {
      document.body.removeChild(fileInput);
    }, 1000);
  };

  const handleAutoGenerate = () => {
    // console.log('Auto generate clicked');
    if (onAutoGenerateClick) {
      onAutoGenerateClick();
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    // console.log('Searching for:', searchTerm);
    // The filtering is already handled by the useEffect
  };

  const renderActionCards = () => {
    return (
      <>
        <ActionCard 
          name="Create New Art"
          icon="plus"
          // backgroundColor="#ffff00"
          iconColor="yellow"
          onClick={handleCreateNewArt}
        />
        
        <ActionCard 
          name="Upload Image"
          icon="upload"
          // backgroundColor="#00ffff"
          iconColor="deepskyblue"
          onClick={handleUploadImage}
          disabled={isGeneratingGame || isUpdatingGameArt}
        />
        
        <ActionCard 
          name="Auto-Generate Art"
          icon="sparkle"
          // backgroundColor="#ff42a8"
          iconColor="deeppink"
          onClick={handleAutoGenerate}
          disabled={isGeneratingGame || isUpdatingGameArt}
        />
      </>
    );
  };

  const renderAIButton = () => {
    return (
      isMobile ? (
        <Tooltip text="AI-Generate Art" colorMode="dark" >
          <Button
              size="tiny"
              color="black" 
              rainbowShimmer={true}
              icon="sparkle" 
              iconPosition="left"
              onClick={handleAutoGenerate} 
              disabled={isGeneratingGame || isUpdatingGameArt}
          />
        </Tooltip>
      ) : (
        <Button
            size="tiny"
            color="black" 
            rainbowShimmer={true}
            icon="sparkle" 
            iconPosition="left"
            text="AI Generate"
            onClick={handleAutoGenerate} 
            disabled={isGeneratingGame || isUpdatingGameArt}
        />
      )
    );
  };

  return (
    <MenuPanel
        open={isOpen}
        onOpenChange={onOpenChange}
        position='left'
        colorMode="dark"
        isCollapsible={false}
        // width={600}
        // overlay={true}
        title={
          <TitleContainer>
            <Together>
              {renderAIButton()}
              <Tooltip text="Upload Image" colorMode="dark" >
                  <Button
                      size="tiny"
                      color="ghost white" 
                      icon="upload" 
                      onClick={handleUploadImage} 
                      disabled={isGeneratingGame || isUpdatingGameArt}
                  />
              </Tooltip>
              <Tooltip text="Create New Art" colorMode="dark" >
                  <Button
                      size="tiny"
                      color="ghost white" 
                      icon="plus" 
                      onClick={handleCreateNewArt} 
                  />
              </Tooltip>
            </Together>
            
            <Together>
              <Search 
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                onSearch={handleSearch}
              />
              <Tooltip text="Close" colorMode="dark" >
                  <Button
                      size="tiny"
                      color="ghost white" 
                      icon="delete" 
                      onClick={() => onOpenChange(false)} 
                  />
              </Tooltip>
            </Together>
          </TitleContainer>
        }
    >
    <Root>
        {userLoading || (loading && communityLoading && myArt.length === 0 && communityArt.length === 0) ? (
          <LoadingSpinner colorMode='dark' />
        ) : (
          <>
            {/* My Art Section */}
            {loading && myArt.length === 0 && (
              <LoadingSpinner colorMode='dark' />
            )}
            
            {filteredMyArt.length > 0 && (
              <>
                {/* <Title>My Art</Title> */}
                <Grid>
                  {filteredMyArt.map((art, index) => (
                    <ArtAsset 
                      key={art.id} 
                      art={{
                        name: art.art_name,
                        image: art.aiImage || art.userPng || ''
                      }}
                      onClick={() => onArtSelected(art.id, art.aiImage, art.art_name)}
                      isDisabled={isGeneratingGame || isUpdatingGameArt}
                    />
                  ))}
                </Grid>
                {loading && (
                  <LoadingSpinner colorMode='dark' size="small"/>
                )}
                {hasMore && searchTerm.trim() === '' && (
                  <LoadingRef ref={loadingRef} />
                )}
              </>
            )}
            
            {/* Show loading spinner when initially loading community art */}
            {communityLoading && communityPage === 1 && communityArt.length === 0 && (
              <LoadingSpinner colorMode='dark' />
            )}
            
            {/* Show community art grid when available */}
            {filteredCommunityArt.length > 0 && (
              <>
                <Title style={{ marginTop: '16px' }}>Community Art</Title>
                <Grid>
                  {filteredCommunityArt.map((art, index) => (
                    <ArtAsset 
                      key={art.id} 
                      art={{
                        name: art.art_name,
                        image: art.aiImage || art.userPng || ''
                      }}
                      onClick={() => onArtSelected(art.id, art.aiImage, art.art_name)}
                      isDisabled={isGeneratingGame || isUpdatingGameArt}
                    />
                  ))}
                </Grid>
                {communityLoading && communityPage > 1 && (
                  <LoadingSpinner colorMode='dark' size="small"/>
                )}
                {communityHasMore && searchTerm.trim() === '' && (
                  <LoadingRef ref={communityLoadingRef} />
                )}
              </>
            )}

            {/* Empty State - only show if both filtered art arrays are empty */}
            {!loading && !communityLoading && filteredMyArt.length === 0 && filteredCommunityArt.length === 0 && (
              <EmptyStateContainer>
                {searchTerm.trim() !== '' ? (
                  <>
                    <Icon icon="search" color={Colors.grey6} size={48} />
                    <EmptyStateText>No matching art found</EmptyStateText>
                  </>
                ) : (
                  <>
                    <EmptyStateText>No art created yet.</EmptyStateText>
                    <Icon icon="image" color={Colors.grey6} size={48} />
                    <EmptyStateText>Create, upload, or auto-generate art to get started.</EmptyStateText>
                  </>
                )}
              </EmptyStateContainer>
            )}
          </>
        )}
    </Root>
    </MenuPanel>
  );
};

export default ArtMenu;